import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Package, Download, Shield, Users, Star } from 'lucide-react';
import { useFeatureFlag } from '../../hooks/useFeatureFlag';
import { capture } from '../../lib/posthog';

const HEADLINES = {
  control: {
    lead: 'Helping Parents Build Calmer Homes, Stronger Relationships, and',
    accent: 'Confident Children.',
  },
  systems: { lead: 'Peaceful Parenting Starts With', accent: 'Simple, Proven Systems' },
  results: { lead: 'End Bedtime Battles &', accent: 'Daily Meltdowns — For Good' },
};

const SOCIAL_PROOF = {
  control: 'Trusted by 10,000+ families worldwide',
  rating: 'Rated 4.9/5 by 10,000+ parents',
  expert: 'Built with parenting experts & researchers',
};

const HeroSection = ({ content }) => {
  // A/B test variants — gracefully fall back to "control" when PostHog isn't configured
  const headlineVariant = useFeatureFlag('hero_headline', 'control');
  const ctaColorVariant = useFeatureFlag('hero_cta_color', 'control');
  const socialProofVariant = useFeatureFlag('hero_social_proof', 'control');

  // CMS content overrides A/B defaults when populated (CMS wins)
  const cmsHeadline = (content?.headline_lead || content?.headline_accent)
    ? { lead: content.headline_lead || '', accent: content.headline_accent || '' }
    : null;
  const headline = cmsHeadline || HEADLINES[headlineVariant] || HEADLINES.control;
  const socialProof = content?.eyebrow || SOCIAL_PROOF[socialProofVariant] || SOCIAL_PROOF.control;
  const subheadline = content?.subheadline || 'Practical printable parenting tools designed for real family life.';
  const primaryCtaText = content?.primary_cta_text || 'Explore Parenting Toolkits';
  const primaryCtaUrl = content?.primary_cta_url || '/shop';
  const secondaryCtaText = content?.secondary_cta_text || 'Download Free Starter Guide';
  const secondaryCtaUrl = content?.secondary_cta_url || '/free-resources';
  const trustMetrics = (Array.isArray(content?.trust_metrics) && content.trust_metrics.length)
    ? content.trust_metrics
    : [
        { value: '50,000+', label: 'Downloads' },
        { value: '10,000+', label: 'Families' },
        { value: 'Instant', label: 'Download' },
      ];

  const ctaClasses =
    ctaColorVariant === 'navy'
      ? 'bg-[#0B1F3A] hover:bg-[#1E293B] text-white shadow-[#0B1F3A]/25'
      : ctaColorVariant === 'orange'
      ? 'bg-[#E9AA5A] hover:bg-[#D89945] text-[#0B1F3A] shadow-[#E9AA5A]/30'
      : 'bg-[#0F766E] hover:bg-[#0D6B64] text-white shadow-[#0F766E]/20';

  // Record hero impression so PostHog can compute experiment denominators
  useEffect(() => {
    capture('hero_viewed', {
      headline_variant: headlineVariant,
      cta_color_variant: ctaColorVariant,
      social_proof_variant: socialProofVariant,
    });
  }, [headlineVariant, ctaColorVariant, socialProofVariant]);

  const trackCta = (placement) => () =>
    capture('cta_click', {
      cta_location: placement,
      cta_color_variant: ctaColorVariant,
      headline_variant: headlineVariant,
      social_proof_variant: socialProofVariant,
    });

  return (
    <section className="relative overflow-hidden" data-testid="hero-section">
      {/* Soft Teal-to-Light Gradient Background per Visual Standards */}
      <div className="absolute inset-0 bg-gradient-to-br from-[#F0FDFA] via-[#F8FAFC] to-white"></div>
      
      {/* Subtle Parenting Silhouette (max 5% opacity) */}
      <div className="absolute inset-0 opacity-[0.03]">
        <div className="absolute bottom-0 right-1/4 w-96 h-96">
          {/* Abstract family silhouette shape */}
          <svg viewBox="0 0 200 200" className="w-full h-full text-[#0F766E]">
            <ellipse cx="70" cy="120" rx="25" ry="60" fill="currentColor"/>
            <ellipse cx="130" cy="130" rx="20" ry="50" fill="currentColor"/>
            <ellipse cx="100" cy="150" rx="15" ry="35" fill="currentColor"/>
            <circle cx="70" cy="50" r="20" fill="currentColor"/>
            <circle cx="130" cy="70" r="16" fill="currentColor"/>
            <circle cx="100" cy="105" r="12" fill="currentColor"/>
          </svg>
        </div>
      </div>
      
      <div className="wrap relative z-10">
        {/* High Whitespace as per guidelines */}
        <div className="py-20 lg:py-32 text-center max-w-4xl mx-auto">
          {/* Social Proof - Metrics Based */}
          <div className="inline-flex items-center gap-3 bg-[#0F766E]/5 border border-[#0F766E]/10 text-[#0F766E] px-5 py-2.5 rounded-full text-sm font-medium mb-8" data-testid="hero-social-proof">
            <Users className="h-4 w-4" />
            <span>{socialProof}</span>
            <div className="flex items-center gap-1 pl-3 border-l border-[#0F766E]/20">
              <Star className="h-3.5 w-3.5 fill-[#F59E0B] text-[#F59E0B]" />
              <span className="font-semibold">4.9</span>
            </div>
          </div>
          
          {/* Main Headline - Calm, Structured, Premium Feel */}
          <h1 className="text-4xl sm:text-5xl lg:text-[56px] font-bold leading-[1.1] mb-6 text-[#0B1F3A]" data-testid="hero-headline">
            {headline.lead}{' '}
            <span className="text-[#0F766E]">
              {headline.accent}
            </span>
          </h1>
          
          {/* Subheadline - per IA spec */}
          <p className="text-lg sm:text-xl text-[#475569] max-w-2xl mx-auto mb-12 leading-relaxed">
            {subheadline}
          </p>
          
          {/* CTA Buttons - per IA spec: Primary → Shop, Secondary → Free Resources */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-16">
            <Link 
              to={primaryCtaUrl}
              className={`group inline-flex items-center justify-center gap-3 font-semibold px-8 py-4 rounded-full transition-all shadow-lg hover:-translate-y-0.5 ${ctaClasses}`}
              data-testid="hero-primary-cta"
              onClick={trackCta('hero_primary')}
            >
              <Package className="h-5 w-5" />
              {primaryCtaText}
              <ArrowRight className="h-5 w-5 group-hover:translate-x-1 transition-transform" />
            </Link>
            <Link 
              to={secondaryCtaUrl}
              className="inline-flex items-center justify-center gap-2 bg-white hover:bg-[#F8FAFC] text-[#0B1F3A] font-semibold px-8 py-4 rounded-full border border-[#E2E8F0] hover:border-[#0F766E]/30 transition-all"
              data-testid="hero-secondary-cta"
              onClick={trackCta('hero_secondary')}
            >
              <Download className="h-4 w-4" />
              {secondaryCtaText}
            </Link>
          </div>
          
          {/* Trust Badges - Minimal, Clean */}
          <div className="flex flex-wrap justify-center gap-10 sm:gap-14">
            {trustMetrics.map((m, i) => {
              const icons = [Download, Users, Shield];
              const Icon = icons[i % icons.length];
              return <TrustMetric key={`${m.label}-${i}`} icon={Icon} value={m.value} label={m.label} />;
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

const TrustMetric = ({ icon: Icon, value, label }) => (
  <div className="flex items-center gap-3 text-[#475569]">
    <div className="w-10 h-10 rounded-xl bg-[#0F766E]/10 flex items-center justify-center">
      <Icon className="h-5 w-5 text-[#0F766E]" />
    </div>
    <div className="text-left">
      <p className="text-lg font-bold text-[#0B1F3A]">{value}</p>
      <p className="text-sm text-[#475569]">{label}</p>
    </div>
  </div>
);

export default HeroSection;
